import React, { useState } from 'react';
import { Button, Confirmation, Input, Loader, useToasts } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, ResolverOptions, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import WorkflowCondition from 'routes/screens/wflow/screens/components/wf-condition';
import WorkflowEvent from 'routes/screens/wflow/screens/components/wf-event';
import Icon from 'shared/components/Icon';
import SwitchButton from 'shared/components/SwitchButton';
import { useGetSurvey } from 'shared/hooks';
import { Center, FreshLink } from 'shared/styles';
import { WorkflowType, WorkflowTypeOptions } from 'shared/types';
import { useAccessControl } from 'utils/access-control';
import { WorkFlowTypeOptionType } from '../types';
import WorkflowAction from './components/wf-action';
import { useSaveWorkflow, useWorkflow } from '../hooks';
import { getWorkflowFormValues, getWorkflowSchema } from '../utils';
import { EditWorkflowContainer } from '../styles';
import { WorkFlowTypeOptions } from '../constants';

const EditWorkflow = () => {
  const { id: workflowId }: { id: string } = useParams();
  const [, setToast] = useToasts();
  const { push } = useHistory();
  const { t } = useTranslation();
  const { state: locationState } = useLocation();
  const { access = [] } = useAccessControl();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { data: workflow, isLoading: isLoadingWorkflow } =
    useWorkflow(workflowId);
  const { mutate: saveWorkflow, isLoading: isSavingWorkflow } = useSaveWorkflow(
    {
      setToast,
      callback: (
        isSuccess: boolean,
        response: WorkflowType,
        variables: WorkflowType,
      ) => {
        if (isSuccess)
          push('/workflow', { type: variables?.type || 'AUTOMATIC' });
      },
    },
  );

  const form = useForm<WorkflowType>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: (
      formValues: WorkflowType,
      context: any,
      options: ResolverOptions<WorkflowType>,
    ) => {
      return yupResolver(
        getWorkflowSchema({
          formValues,
          surveyData,
        }),
      )(formValues, context, options);
    },
  });

  const {
    watch,
    handleSubmit,
    setValue,
    register,
    reset,
    formState: { errors, submitCount, dirtyFields },
  } = form;

  const event = watch('event');
  const trigger = watch('type');
  const surveyId = watch('survey_id');

  const workflowTypeOptions = React.useMemo(() => {
    return Object.keys(WorkFlowTypeOptions).map((key) => ({
      id: key as WorkflowTypeOptions,
      label: t(WorkFlowTypeOptions[key]),
    }));
  }, [t]);

  const disabled = !access.includes('workflow:write');

  const { data: surveyData, isFetching: isFetchingSurvey } = useGetSurvey({
    surveyId: surveyId || '',
    enabled: !!surveyId,
    callback: (isSuccess) => {
      if (!isSuccess) {
        setValue('survey_id', null);
      }
    },
  });

  React.useEffect(() => {
    if (workflow?.workflow_id) {
      reset(getWorkflowFormValues(workflow));
    }
  }, [workflow, reset]);

  React.useEffect(() => {
    if (!workflowId) {
      reset({
        name: '',
        type: (locationState as any)?.type ?? 'AUTOMATIC',
        condition_groups_operator: 'AND',
        event: 'EVERYTIME_A_SURVEY_IS_RESPONDED',
        actions: [{}],
        active: true,
        condition_groups: [
          {
            conditions_operator: 'OR',
            conditions: [
              {
                variable: 'NPS_RATING',
                operator: 'IS',
                value: '0',
              },
            ],
          },
        ],
      });
    }
  }, [workflowId, reset]);

  if ((workflowId && !workflow) || isLoadingWorkflow)
    return (
      <Center minHeight="calc(100% - 100px)">
        <Loader />
      </Center>
    );

  const onSubmit = (data: WorkflowType) => {
    saveWorkflow(data);
  };

  return (
    <FormProvider {...form}>
      <EditWorkflowContainer>
        <div className="wf-left-pane">
          <div className="wf-left-header">
            <div className="wf-title">
              {workflowId
                ? `${t('workflow.edit_workflow')}`
                : `${t('workflow.new')}`}
            </div>
            <div className="wf-description">{t('workflow.desc')}</div>
            <div className="wf-h-fields">
              <Input
                {...register('name')}
                id="name"
                label={`${t('workflow.table_header.workflow_name')}`}
                disabled={disabled || isSavingWorkflow}
                placeholder={`${t('forms.placeholders.workflow_name')}`}
                isRequired
                isErrored={Boolean(submitCount > 0 && errors.name?.message)}
                errorText={
                  submitCount > 0 && errors.name?.message
                    ? errors.name.message
                    : ''
                }
              />
              <div>
                <div className="wf-sb-label">{t('workflow.trigger') || ''}</div>
                <div className="wf-sb-toggle">
                  <SwitchButton
                    {...register('type')}
                    value={watch('type') || ''}
                    disabled={disabled || isSavingWorkflow}
                    onChange={(item: WorkFlowTypeOptionType) => {
                      if (item.id === trigger || disabled) return;
                      if (item.id === 'MANUAL') {
                        setValue('event', null);
                        setValue('condition_groups', null);
                        setValue('condition_groups_operator', null);
                      }
                      if (item.id === 'AUTOMATIC') {
                        setValue('event', 'EVERYTIME_A_SURVEY_IS_RESPONDED');
                        setValue('condition_groups', [
                          {
                            conditions_operator: 'OR',
                            conditions: [
                              {
                                variable: 'NPS_RATING',
                                operator: 'IS',
                                value: '0',
                              },
                            ],
                          },
                        ]);
                        setValue('condition_groups_operator', 'AND');
                      }
                      setValue('type', item.id);
                    }}
                    items={workflowTypeOptions}
                    itemLabelIdentifier="label"
                    itemValueIdentifier="id"
                  />
                </div>
              </div>
            </div>
          </div>
          {trigger === 'AUTOMATIC' && (
            <>
              <WorkflowEvent disabled={disabled || isSavingWorkflow} />
              {event !== 'EVERYTIME_A_CONTACT_IS_UNSUBSCRIBED' && (
                <WorkflowCondition
                  disabled={disabled || isSavingWorkflow}
                  surveyData={surveyData}
                  isFetchingSurvey={isFetchingSurvey}
                />
              )}
            </>
          )}
          <WorkflowAction disabled={disabled || isSavingWorkflow} />
          <div className="wf-btns">
            <Button
              inline
              disabled={disabled || isSavingWorkflow}
              onClick={() => {
                handleSubmit(onSubmit, (err) => console.log('ERR', err))();
              }}
            >
              {t('save')}
            </Button>
            <Button
              type="secondary"
              htmlType="button"
              inline
              onClick={() => {
                if (Object.keys(dirtyFields).length)
                  setIsConfirmationOpen(true);
                else push('/workflow');
              }}
            >
              {t('cancel')}
            </Button>
          </div>
        </div>
        <div className="wf-right-pane">
          <div className="wf-title">{t('sidebar.workflows')}</div>
          <div className="wf-description">
            {t('workflow.create.desc')}
            <br />
            <br />
            <FreshLink
              href="https://support.freshsurvey.io/en/support/solutions/articles/50000004057-workflows-an-overview"
              target="_blank"
              rel="noreferrer"
            >
              {t('workflow.create.learn_to_create')}
            </FreshLink>
          </div>
        </div>
        <Confirmation
          width="540px"
          isOpen={isConfirmationOpen}
          onDismiss={() => setIsConfirmationOpen(false)}
          title={`${t('confirm_navigation')}`}
          cancelButtonText={`${t('cancel')}`}
          confirmButtonText={`${t('yes')}`}
          onConfirm={() => push('/workflow')}
        >
          <StyledConfirmation>
            <div className="description">
              {t('forms.confirmation.confirm_to_proceed')}
            </div>
            <div className="info">
              <Icon fill="#d7461f" width="16" height="16" glyph="info" />
              <div className="divider" />
              <div className="text-content">
                {t('forms.confirmation.action_irreversible')}
              </div>
            </div>
          </StyledConfirmation>
        </Confirmation>
      </EditWorkflowContainer>
    </FormProvider>
  );
};

const StyledConfirmation = styled.div`
  color: #475867;
  font-size: 14px;
  line-height: 20px;
  .info {
    display: flex;
    background: #fef1e1;
    border: 1px solid #fddbb5;
    padding: 4px 12px;
    border-radius: 4px;
    margin-top: 12px;
    align-items: center;
  }
  .divider {
    background: #fddbb5;
    width: 1px;
    height: 24px;
    margin: 0 8px;
  }
`;

export default EditWorkflow;
